import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../api/remembrApi';
import type { RootState } from '../../store/store';
import type {
  HandoffRequest,
  HandoffRequestRequest,
  HandoffRequestResponse,
  ReturnRequest,
  CustodyEventSummary,
  CustodyHistoryResponse,
  CustodyFlipResponse,
  RecallRequest,
  RecallResponse,
} from '../../api/types';

/**
 * Custody slice — Sprint 3 / Chunk 10A.
 *
 * Holds per-item custody history (the `item_custody_event` log) and
 * exposes thunks for the four custody-mutating endpoints:
 *
 *   - POST /inventory/{id}/handoff           (owner-push, custody flips immediately)
 *   - POST /inventory/{id}/handoff-request   (member-pull, may auto-approve)
 *   - POST /inventory/{id}/return            (current holder returns)
 *   - GET  /inventory/{id}/custody           (paginated history)
 *
 * Cross-slice update: `browseSlice` listens to `handoffItem.fulfilled` /
 * `returnItem.fulfilled` and patches `inventoryItemsEnhanced[i]` with
 * the new custodian + due_date so Browse cards re-render with fresh
 * custody chips without a separate refetch.
 *
 * State shape:
 *   - historyByItemId: { [itemId]: CustodyHistoryResponse }
 *   - loadingByItemId: { [itemId]: { history, handoff, request, return } }
 *   - errorByItemId:   { [itemId]: string | null }
 */

interface PerItemLoading {
  history: boolean;
  handoff: boolean;
  request: boolean;
  return: boolean;
  recall: boolean;
  recallAck: boolean;
}

const EMPTY_LOADING: PerItemLoading = {
  history: false,
  handoff: false,
  request: false,
  return: false,
  recall: false,
  recallAck: false,
};

interface CustodyState {
  historyByItemId: Record<number, CustodyHistoryResponse>;
  loadingByItemId: Record<number, PerItemLoading>;
  errorByItemId: Record<number, string | null>;
}

const initialState: CustodyState = {
  historyByItemId: {},
  loadingByItemId: {},
  errorByItemId: {},
};

/* ──────────────────────────────────────────────────────────────────────
 * Thunks
 * ────────────────────────────────────────────────────────────────────── */

export const loadCustodyHistory = createAsyncThunk<
  { itemId: number; data: CustodyHistoryResponse },
  number,
  { rejectValue: string }
>(
  'custody/loadCustodyHistory',
  async (itemId, { rejectWithValue }) => {
    try {
      const data = await api.getCustodyHistory(itemId);
      return { itemId, data };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail || err?.message || 'Failed to load custody history',
      );
    }
  },
);

/** Owner-push handoff. Custody flips immediately on success. */
export const handoffItem = createAsyncThunk<
  { itemId: number; data: CustodyFlipResponse },
  { itemId: number; request: HandoffRequest },
  { rejectValue: string }
>(
  'custody/handoffItem',
  async ({ itemId, request }, { rejectWithValue }) => {
    try {
      const data = await api.handoffItem(itemId, request);
      return { itemId, data };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail || err?.message || 'Handoff failed',
      );
    }
  },
);

/** Member-pull "ask to borrow". Server may auto-approve (custody flips
 *  + event written) OR queue pending. Inspect `auto_approved` on the
 *  response to decide UX feedback. */
export const requestHandoff = createAsyncThunk<
  { itemId: number; data: HandoffRequestResponse },
  { itemId: number; request: HandoffRequestRequest },
  { rejectValue: string }
>(
  'custody/requestHandoff',
  async ({ itemId, request }, { rejectWithValue }) => {
    try {
      const data = await api.requestHandoff(itemId, request);
      return { itemId, data };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail || err?.message || 'Handoff request failed',
      );
    }
  },
);

/** Owner forces a return on an item currently held by someone else.
 *  Backend writes a high-priority borrow_requests row + emails the
 *  holder. Custody DOES NOT flip immediately — the holder still has
 *  to act on the recall. Sprint 4 / Chunk 10B FE-10.3. */
export const recallItem = createAsyncThunk<
  { itemId: number; data: RecallResponse },
  { itemId: number; request?: RecallRequest },
  { rejectValue: string }
>(
  'custody/recallItem',
  async ({ itemId, request = {} }, { rejectWithValue }) => {
    try {
      const data = await api.recallItem(itemId, request);
      return { itemId, data };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail || err?.message || 'Recall failed',
      );
    }
  },
);

/** Holder returns the item. Custody flips back to owner. */
export const returnItem = createAsyncThunk<
  { itemId: number; data: CustodyFlipResponse },
  { itemId: number; request?: ReturnRequest },
  { rejectValue: string }
>(
  'custody/returnItem',
  async ({ itemId, request = {} }, { rejectWithValue }) => {
    try {
      const data = await api.returnItem(itemId, request);
      return { itemId, data };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail || err?.message || 'Return failed',
      );
    }
  },
);

/** Sprint 8 / FE-132 (BE-147) — current custodian acks an outstanding
 *  recall. Custody flips back to owner; the RECALL borrow_requests row
 *  is marked FULFILLED server-side; a RECALL_ACK custody event is
 *  written; the owner gets a CUSTODY_RECALL_ACK notification.
 *
 *  Response shape is identical to `returnItem` — same `CustodyFlipResponse`
 *  envelope, so the reducer can reuse the same `appendEvent` flow. */
export const acknowledgeRecall = createAsyncThunk<
  { itemId: number; data: CustodyFlipResponse },
  { itemId: number },
  { rejectValue: string }
>(
  'custody/acknowledgeRecall',
  async ({ itemId }, { rejectWithValue }) => {
    try {
      const data = await api.acknowledgeRecall(itemId);
      return { itemId, data };
    } catch (err: any) {
      return rejectWithValue(
        err?.response?.data?.detail ||
          err?.message ||
          'Acknowledge recall failed',
      );
    }
  },
);

/** FE-222 / SG-16 — owner nudges current custodian. Writes a REMINDER
 *  event in `item_custody_event` for audit + fires email + in-app
 *  `CUSTODY_REMINDER` notification. 24h server-side rate limit per
 *  (owner, item). On 429 the response carries `next_reminder_allowed_at`
 *  so the UI can show a cooldown timer instead of a generic error. */
export const remindCustodian = createAsyncThunk<
  {
    itemId: number;
    remindedAt: string;
    nextAllowedAt: string;
    daysHeld: number;
  },
  { itemId: number; customMessage?: string },
  { rejectValue: { detail: string; nextAllowedAt?: string } }
>(
  'custody/remindCustodian',
  async ({ itemId, customMessage }, { rejectWithValue }) => {
    try {
      const data = await api.remindCustodian(
        itemId,
        customMessage ? { custom_message: customMessage } : {},
      );
      return {
        itemId,
        remindedAt: data.reminded_at,
        nextAllowedAt: data.next_reminder_allowed_at,
        daysHeld: data.days_held,
      };
    } catch (err: any) {
      // Bubble up the structured payload so the UI can render the
      // cooldown timer when the server returns 429.
      const ctx = err?.response?.data?.context;
      return rejectWithValue({
        detail:
          err?.response?.data?.detail ||
          err?.message ||
          'Reminder failed',
        nextAllowedAt:
          ctx?.next_reminder_allowed_at ?? ctx?.next_allowed_at,
      });
    }
  },
);

/* ──────────────────────────────────────────────────────────────────────
 * Slice
 * ────────────────────────────────────────────────────────────────────── */

function ensureLoading(state: CustodyState, itemId: number): PerItemLoading {
  if (!state.loadingByItemId[itemId]) {
    state.loadingByItemId[itemId] = { ...EMPTY_LOADING };
  }
  return state.loadingByItemId[itemId];
}

/** Prepend a new event to the cached history (newest first), bump
 *  totals, update top-level custody fields. Idempotent on event id. */
function appendEvent(
  state: CustodyState,
  itemId: number,
  event: CustodyEventSummary,
  patch: Partial<Pick<CustodyHistoryResponse, 'current_custodian_user_id' | 'custody_since' | 'custody_due_date'>>,
) {
  const existing = state.historyByItemId[itemId];
  if (!existing) {
    // Skip cache update if we haven't loaded history yet — it'll be
    // fresh next time the user opens the timeline.
    return;
  }
  const alreadyHas = existing.events.some((e) => e.id === event.id);
  state.historyByItemId[itemId] = {
    ...existing,
    ...patch,
    total: alreadyHas ? existing.total : existing.total + 1,
    events: alreadyHas ? existing.events : [event, ...existing.events],
  };
}

const custodySlice = createSlice({
  name: 'custody',
  initialState,
  reducers: {
    clearCustodyError(state, action: { payload: number }) {
      state.errorByItemId[action.payload] = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // loadCustodyHistory
      .addCase(loadCustodyHistory.pending, (state, action) => {
        const itemId = action.meta.arg;
        ensureLoading(state, itemId).history = true;
        state.errorByItemId[itemId] = null;
      })
      .addCase(loadCustodyHistory.fulfilled, (state, action) => {
        const { itemId, data } = action.payload;
        ensureLoading(state, itemId).history = false;
        state.historyByItemId[itemId] = data;
      })
      .addCase(loadCustodyHistory.rejected, (state, action) => {
        const itemId = action.meta.arg;
        ensureLoading(state, itemId).history = false;
        state.errorByItemId[itemId] =
          (action.payload as string) ?? action.error.message ?? 'Error';
      })

      // handoffItem
      .addCase(handoffItem.pending, (state, action) => {
        const { itemId } = action.meta.arg;
        ensureLoading(state, itemId).handoff = true;
        state.errorByItemId[itemId] = null;
      })
      .addCase(handoffItem.fulfilled, (state, action) => {
        const { itemId, data } = action.payload;
        ensureLoading(state, itemId).handoff = false;
        appendEvent(state, itemId, data.event, {
          current_custodian_user_id: data.current_custodian_user_id,
          custody_since: data.custody_since,
          custody_due_date: data.custody_due_date,
        });
      })
      .addCase(handoffItem.rejected, (state, action) => {
        const { itemId } = action.meta.arg;
        ensureLoading(state, itemId).handoff = false;
        state.errorByItemId[itemId] =
          (action.payload as string) ?? action.error.message ?? 'Error';
      })

      // requestHandoff
      .addCase(requestHandoff.pending, (state, action) => {
        const { itemId } = action.meta.arg;
        ensureLoading(state, itemId).request = true;
        state.errorByItemId[itemId] = null;
      })
      .addCase(requestHandoff.fulfilled, (state, action) => {
        const { itemId, data } = action.payload;
        ensureLoading(state, itemId).request = false;
        // Only update history if this auto-approved (custody actually flipped).
        if (data.auto_approved && data.event) {
          appendEvent(state, itemId, data.event, {
            current_custodian_user_id: data.current_custodian_user_id,
          });
        }
      })
      .addCase(requestHandoff.rejected, (state, action) => {
        const { itemId } = action.meta.arg;
        ensureLoading(state, itemId).request = false;
        state.errorByItemId[itemId] =
          (action.payload as string) ?? action.error.message ?? 'Error';
      })

      // recallItem
      .addCase(recallItem.pending, (state, action) => {
        const { itemId } = action.meta.arg;
        ensureLoading(state, itemId).recall = true;
        state.errorByItemId[itemId] = null;
      })
      .addCase(recallItem.fulfilled, (state, action) => {
        const { itemId } = action.payload;
        ensureLoading(state, itemId).recall = false;
        // No history mutation — recall doesn't flip custody. The
        // backend writes the borrow_requests row but no
        // item_custody_event yet (that fires when the holder
        // acknowledges via RECALL_ACK).
      })
      .addCase(recallItem.rejected, (state, action) => {
        const { itemId } = action.meta.arg;
        ensureLoading(state, itemId).recall = false;
        state.errorByItemId[itemId] =
          (action.payload as string) ?? action.error.message ?? 'Error';
      })

      // returnItem
      .addCase(returnItem.pending, (state, action) => {
        const { itemId } = action.meta.arg;
        ensureLoading(state, itemId).return = true;
        state.errorByItemId[itemId] = null;
      })
      .addCase(returnItem.fulfilled, (state, action) => {
        const { itemId, data } = action.payload;
        ensureLoading(state, itemId).return = false;
        appendEvent(state, itemId, data.event, {
          current_custodian_user_id: data.current_custodian_user_id,
          custody_since: data.custody_since,
          custody_due_date: data.custody_due_date,
        });
      })
      .addCase(returnItem.rejected, (state, action) => {
        const { itemId } = action.meta.arg;
        ensureLoading(state, itemId).return = false;
        state.errorByItemId[itemId] =
          (action.payload as string) ?? action.error.message ?? 'Error';
      })

      // acknowledgeRecall (Sprint 8 / FE-132 — BE-147)
      // Shape is identical to returnItem (CustodyFlipResponse) so we
      // reuse the same `appendEvent` flow. Only difference is the
      // spinner key + the event.action='RECALL_ACK' rather than RETURN.
      .addCase(acknowledgeRecall.pending, (state, action) => {
        const { itemId } = action.meta.arg;
        ensureLoading(state, itemId).recallAck = true;
        state.errorByItemId[itemId] = null;
      })
      .addCase(acknowledgeRecall.fulfilled, (state, action) => {
        const { itemId, data } = action.payload;
        ensureLoading(state, itemId).recallAck = false;
        appendEvent(state, itemId, data.event, {
          current_custodian_user_id: data.current_custodian_user_id,
          custody_since: data.custody_since,
          custody_due_date: data.custody_due_date,
        });
      })
      .addCase(acknowledgeRecall.rejected, (state, action) => {
        const { itemId } = action.meta.arg;
        ensureLoading(state, itemId).recallAck = false;
        state.errorByItemId[itemId] =
          (action.payload as string) ?? action.error.message ?? 'Error';
      });
  },
});

export const { clearCustodyError } = custodySlice.actions;
export default custodySlice.reducer;

/* ──────────────────────────────────────────────────────────────────────
 * Selectors
 * ────────────────────────────────────────────────────────────────────── */

export const selectCustodyHistory =
  (itemId: number) =>
  (state: RootState): CustodyHistoryResponse | undefined =>
    state.custody.historyByItemId[itemId];

export const selectCustodyEvents =
  (itemId: number) =>
  (state: RootState): CustodyEventSummary[] =>
    state.custody.historyByItemId[itemId]?.events ?? [];

export const selectCustodyLoading =
  (itemId: number) =>
  (state: RootState): PerItemLoading =>
    state.custody.loadingByItemId[itemId] ?? EMPTY_LOADING;

export const selectCustodyError =
  (itemId: number) =>
  (state: RootState): string | null =>
    state.custody.errorByItemId[itemId] ?? null;

export const selectCurrentCustodian =
  (itemId: number) =>
  (state: RootState): number | null | undefined =>
    state.custody.historyByItemId[itemId]?.current_custodian_user_id;
